import React, { useEffect, useState } from "react";
import { Card, Switch, TimePicker, Button, App } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  apiGetAvailability,
  apiUpdateAvailability,
} from "../../api/availability";

const Availability = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [acceptingAppointments, setAcceptingAppointments] = useState(true);
  const [acceptingEmergency, setAcceptingEmergency] = useState(true);

  const [schedule, setSchedule] = useState([
    {
      day: "Monday",
      dayOfWeek: 1,
      from: dayjs("09:00", "HH:mm"),
      to: dayjs("17:30", "HH:mm"),
      enabled: true,
    },
    {
      day: "Tuesday",
      dayOfWeek: 2,
      from: dayjs("09:00", "HH:mm"),
      to: dayjs("17:30", "HH:mm"),
      enabled: true,
    },
    {
      day: "Wednesday",
      dayOfWeek: 3,
      from: dayjs("09:00", "HH:mm"),
      to: dayjs("17:30", "HH:mm"),
      enabled: true,
    },
    {
      day: "Thursday",
      dayOfWeek: 4,
      from: dayjs("09:00", "HH:mm"),
      to: dayjs("17:30", "HH:mm"),
      enabled: true,
    },
    {
      day: "Friday",
      dayOfWeek: 5,
      from: dayjs("09:00", "HH:mm"),
      to: dayjs("17:30", "HH:mm"),
      enabled: true,
    },
    {
      day: "Saturday",
      dayOfWeek: 6,
      from: dayjs("09:00", "HH:mm"),
      to: dayjs("17:30", "HH:mm"),
      enabled: false,
    },
    {
      day: "Sunday",
      dayOfWeek: 7,
      from: dayjs("09:00", "HH:mm"),
      to: dayjs("17:30", "HH:mm"),
      enabled: false,
    },
  ]);

  useEffect(() => {
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      const response = await apiGetAvailability();
      const data = response?.data;

      if (data) {
        setAcceptingAppointments(data.doctorInfo.acceptingNewAppointments);
        setAcceptingEmergency(data.doctorInfo.acceptingEmergencyCalls);

        if (data.availability && data.availability.length > 0) {
          const updatedSchedule = schedule.map((item) => {
            const apiDay = data.availability.find(
              (d) => d.dayOfWeek === item.dayOfWeek
            );
            if (apiDay) {
              return {
                ...item,
                from: dayjs(apiDay.startTime, "HH:mm"),
                to: dayjs(apiDay.endTime, "HH:mm"),
                enabled: apiDay.isAvailable,
              };
            }
            return item;
          });
          setSchedule(updatedSchedule);
        }
      }
    } catch (error) {
      message.error("Failed to load availability settings");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = (index, field, value) => {
    const newSchedule = [...schedule];
    newSchedule[index][field] = value;
    setSchedule(newSchedule);
  };

  const handleToggleDay = (index) => {
    const newSchedule = [...schedule];
    newSchedule[index].enabled = !newSchedule[index].enabled;
    setSchedule(newSchedule);
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const availability = schedule.map((item) => ({
        dayOfWeek: item.dayOfWeek,
        startTime: item.from.format("HH:mm"),
        endTime: item.to.format("HH:mm"),
        isAvailable: item.enabled,
      }));

      const payload = {
        acceptingNewAppointments: acceptingAppointments,
        acceptingEmergencyCalls: acceptingEmergency,
        availability: availability,
      };

      await apiUpdateAvailability(payload);
      message.success("Availability settings saved successfully!");
    } catch (error) {
      message.error("Failed to save availability settings");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex gap-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftOutlined className="text-xl" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Availability</h1>
          <p className="text-gray-500 text-sm mt-1">
            Set your available times to manage your schedule
          </p>
        </div>
      </div>

      {/* MAIN OUTER CARD */}
      <Card className="shadow-sm p-0">
        {/* Card 1: Accepting New Appointments */}
        <Card className="mb-4 border-0 shadow-none">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Accepting New Appointments
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Toggle to set your status
              </p>
            </div>
            <Switch
              checked={acceptingAppointments}
              onChange={setAcceptingAppointments}
              style={
                acceptingAppointments ? { backgroundColor: "#9333ea" } : {}
              }
            />
          </div>
        </Card>

        {/* Card 2: Accepting Emergency Calls */}
        <Card className="mb-4 border-0 shadow-none">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Accepting Emergency Calls
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Toggle to set your status
              </p>
            </div>
            <Switch
              checked={acceptingEmergency}
              onChange={setAcceptingEmergency}
              style={acceptingEmergency ? { backgroundColor: "#9333ea" } : {}}
            />
          </div>
        </Card>

        {/* Card 3: Set Time */}
        <Card className="border-0 shadow-none">
          <h3 className="text-base font-semibold text-gray-900 mb-6">
            Set time
          </h3>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "37px" }}
          >
            {schedule.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-[120px_1fr_auto] gap-6 items-center"
              >
                {/* Day Name */}
                <div
                  className={`text-sm font-medium ${
                    item.enabled ? "text-gray-900" : "text-gray-400"
                  }`}
                >
                  {item.day}
                </div>

                {/* Time Pickers Section */}
                <div className="grid grid-cols-2 gap-6">
                  {/* From Time */}
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-xs ${
                        item.enabled ? "text-gray-400" : "text-gray-300"
                      }`}
                      style={{ minWidth: "35px" }}
                    >
                      From
                    </span>
                    <TimePicker
                      value={item.from}
                      onChange={(value) =>
                        handleTimeChange(index, "from", value)
                      }
                      format="h:mm A"
                      disabled={!item.enabled}
                      size="middle"
                      className="flex-1"
                      suffixIcon={null}
                      use12Hours
                    />
                  </div>

                  {/* To Time */}
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-xs ${
                        item.enabled ? "text-gray-400" : "text-gray-300"
                      }`}
                      style={{ minWidth: "20px" }}
                    >
                      To
                    </span>
                    <TimePicker
                      value={item.to}
                      onChange={(value) => handleTimeChange(index, "to", value)}
                      format="h:mm A"
                      disabled={!item.enabled}
                      size="middle"
                      className="flex-1"
                      suffixIcon={null}
                      use12Hours
                    />
                  </div>
                </div>

                {/* Toggle Switch */}
                <Switch
                  checked={item.enabled}
                  onChange={() => handleToggleDay(index)}
                  style={item.enabled ? { backgroundColor: "#9333ea" } : {}}
                />
              </div>
            ))}
          </div>

          {/* Set Button */}
          <div className="flex justify-end mt-8">
            <Button
              type="primary"
              size="large"
              onClick={handleSave}
              loading={loading}
              className="bg-purple-600 hover:bg-purple-700 border-0 px-8"
            >
              Set
            </Button>
          </div>
        </Card>
      </Card>
    </div>
  );
};

export default Availability;
